/**
 * Customer-facing title + body for Receipt-Claim backend error codes
 * (Phase R3 web, 2026-06-29).
 *
 * Mirrors `/app/mobile/src/claimErrorUI.ts` byte-for-byte so the web
 * surface and mobile surface deliver the same copy when the backend
 * returns the same `{detail: {code, message}}` payload. Keep the two
 * files in sync when the backend adds a new rejection reason or error
 * code.
 */

/**
 * @typedef {{ title: string, body: string }} ClaimErrorUI
 */

/**
 * @param {string|null|undefined} code
 * @param {string|null|undefined} message
 * @returns {ClaimErrorUI}
 */
export function claimErrorTitleAndBody(code, message) {
  switch (code) {
    // ---- Rate limits (receipt_claims.py:247-289) ----------------------
    case 'too_many_pending':
      return {
        title: 'Too many pending claims',
        body:
          'You already have 5 claims waiting for review. Please wait for one to be approved or rejected before uploading more.',
      };
    case 'brand_already_pending':
      return {
        title: 'Receipt already submitted',
        body:
          'You already have a receipt pending for this brand. Please wait for the brand to review it before uploading another.',
      };

    // ---- Upload validation (receipt_image_service.py:70-108) ----------
    case 'image_too_large':
      return {
        title: 'Photo is too large',
        body: 'Receipt photo must be smaller than 15 MB. Try taking a new photo.',
      };
    case 'image_unsupported':
      return {
        title: 'Unsupported file type',
        body: 'Please upload a photo (JPEG, PNG, or WebP).',
      };
    case 'image_empty':
      return {
        title: 'Upload is empty',
        body: 'The selected file is empty. Please pick a different photo.',
      };

    // ---- Lookup / submit guards --------------------------------------
    case 'brand_not_found':
      return {
        title: 'Brand not found',
        body: 'This brand is no longer available.',
      };
    case 'brand_id_required':
      return {
        title: 'Brand required',
        body:
          'Please go back to the brand page and tap "Claim Stamp from Receipt" from there.',
      };
    case 'claim_not_found':
      return {
        title: 'Claim not found',
        body: 'This claim has been removed.',
      };

    // ---- Owner-side decisions reflected to customer -------------------
    case 'already_approved':
      return {
        title: 'Already approved',
        body: 'This claim has already been approved — your stamp is already on your card.',
      };
    case 'already_rejected':
      return {
        title: 'Already reviewed',
        body: 'This claim has already been reviewed.',
      };
    case 'ai_not_finished':
      return {
        title: 'Still analyzing',
        body: 'Please wait a moment while we finish analyzing your receipt.',
      };
    default:
      return {
        title: 'Something went wrong',
        body: message && String(message).length > 0 ? String(message) : 'Please try again.',
      };
  }
}

/**
 * Human copy for a rejection reason stored on `owner_decision.reason`.
 * Mirrors backend REJECTION_REASONS set.
 */
export function rejectionReasonLabel(reason) {
  switch (String(reason || '').toLowerCase()) {
    case 'duplicate':
      return 'This receipt was already submitted and approved.';
    case 'unreadable':
      return 'The receipt photo was too blurry or unclear to verify.';
    case 'brand_mismatch':
      return 'The receipt does not appear to be from this brand.';
    case 'expired':
      return 'This receipt is older than the brand allows for claims.';
    case 'fake_or_edited':
      return 'The brand could not verify this receipt as authentic.';
    case 'other':
      return 'The brand could not approve this receipt.';
    default:
      return 'The brand could not approve this receipt.';
  }
}

/**
 * Short label used in owner-side filters, dropdowns, and badges.
 */
export function rejectionReasonShort(reason) {
  switch (String(reason || '').toLowerCase()) {
    case 'duplicate':      return 'Duplicate';
    case 'unreadable':     return 'Unreadable';
    case 'brand_mismatch': return 'Brand mismatch';
    case 'expired':        return 'Expired';
    case 'fake_or_edited': return 'Fake / edited';
    case 'other':          return 'Other';
    default:               return 'Rejected';
  }
}

/**
 * Enum of valid rejection reasons (owner-side modal dropdown source).
 * Mirrors `REJECTION_REASONS` in `/app/backend/receipt_claims.py`.
 */
export const REJECTION_REASONS = Object.freeze([
  { value: 'brand_mismatch', label: 'Brand mismatch', hint: 'Receipt is not from this brand' },
  { value: 'unreadable',     label: 'Unreadable',     hint: 'Photo too blurry or unclear' },
  { value: 'expired',        label: 'Expired',        hint: 'Receipt is older than allowed' },
  { value: 'duplicate',      label: 'Duplicate',      hint: 'Same receipt already approved' },
  { value: 'fake_or_edited', label: 'Fake / edited',  hint: 'Receipt appears manipulated' },
  { value: 'other',          label: 'Other',          hint: 'Add notes to explain' },
]);

/**
 * Formatter for currency amounts — Indonesian Rupiah ("Rp 50.000").
 * Shared between customer + owner surfaces so the two sides render
 * the exact same total.
 */
export function formatRupiah(amount) {
  if (amount == null || Number.isNaN(Number(amount))) return '—';
  const n = Math.round(Number(amount));
  const grouped = String(Math.abs(n)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${n < 0 ? '-' : ''}Rp ${grouped}`;
}

/**
 * Format a claim status to a user-friendly label.
 */
export function claimStatusLabel(status) {
  switch (status) {
    case 'pending_ai_analysis':  return 'Analyzing';
    case 'pending_owner_review': return 'Pending';
    case 'approved':             return 'Approved';
    case 'rejected':             return 'Rejected';
    default:                     return status || '—';
  }
}
